import { RULES_VERSION } from '../../engine';
import { t } from '../../i18n';
import { APP_VERSION } from '../../app/version';
import { FullScreenOffer } from '../components/FullScreenOffer';
import { PawnFigure } from '../components/PawnFigure';

export type MenuTarget =
  | 'continue'
  | 'tutorial'
  | 'duel'
  | 'campaign'
  | 'night'
  | 'online'
  | 'leaderboard'
  | 'replays'
  | 'rules'
  | 'settings';

export function TitleScreen({
  canContinue,
  notice,
  sharing,
  tutorial,
  night = null,
  onSelect,
}: {
  canContinue: boolean;
  /** A Long Night run under way: where it stands ("Night II · Big Table"), to resume it. */
  night?: string | null;
  notice: string | null;
  /** Matches are uploaded for playtest analysis (the shareMatches setting). */
  sharing: boolean;
  /** How far the player got in the tutorial: until it is done, it leads the menu. */
  tutorial: 'new' | 'started' | 'completed';
  onSelect: (m: MenuTarget) => void;
}) {
  const learning = tutorial !== 'completed';
  return (
    <main className="title-screen">
      <div className="title-art" aria-hidden="true">
        <span className="title-pawn title-pawn-n">
          <PawnFigure side="N" crowned={false} immune={false} inscription={null} />
        </span>
        <span className="title-pawn title-pawn-s">
          <PawnFigure side="S" crowned immune={false} inscription={null} />
        </span>
      </div>
      <h1 className="logo">{t('app.title')}</h1>
      <p className="tagline">{t('app.tagline')}</p>
      {notice && (
        <p className="notice" role="alert">
          {notice}
        </p>
      )}
      <nav className="menu" aria-label={t('menu.mainMenu')}>
        {canContinue && (
          <button
            type="button"
            className="btn btn-primary btn-large"
            onClick={() => onSelect('continue')}
          >
            {t('menu.continueDuel')}
          </button>
        )}
        {learning && (
          <button
            type="button"
            className={`btn btn-large ${canContinue ? '' : 'btn-primary'}`}
            onClick={() => onSelect('tutorial')}
          >
            {tutorial === 'started' ? t('menu.tutorialResume') : t('menu.tutorial')}
            <span className="btn-sub">{t('menu.tutorialHint')}</span>
          </button>
        )}
        <button
          type="button"
          className={`btn btn-large ${canContinue || learning ? '' : 'btn-primary'}`}
          onClick={() => onSelect('duel')}
        >
          {t('menu.newDuel')}
        </button>
        <button type="button" className="btn btn-large" onClick={() => onSelect('campaign')}>
          {t('menu.campaign')}
          <span className="btn-sub">{t('menu.campaignHint')}</span>
        </button>
        <button type="button" className="btn btn-large btn-night" onClick={() => onSelect('night')}>
          {t('menu.night')}
          <span className="btn-sub">
            {night ? t('menu.nightResume', { where: night }) : t('menu.nightHint')}
          </span>
        </button>
        <button type="button" className="btn btn-large" onClick={() => onSelect('online')}>
          {t('menu.online')}
          <span className="btn-sub">{t('menu.onlineHint')}</span>
        </button>
        <div className="menu-row">
          <button type="button" className="btn" onClick={() => onSelect('rules')}>
            {t('menu.rules')}
          </button>
          <button type="button" className="btn" onClick={() => onSelect('replays')}>
            {t('menu.replays')}
          </button>
          <button type="button" className="btn" onClick={() => onSelect('leaderboard')}>
            {t('menu.leaderboard')}
          </button>
          <button type="button" className="btn" onClick={() => onSelect('settings')}>
            {t('menu.settings')}
          </button>
          {!learning && (
            <button type="button" className="btn" onClick={() => onSelect('tutorial')}>
              {t('menu.tutorialAgain')}
            </button>
          )}
        </div>
      </nav>
      <FullScreenOffer />
      <footer className="title-footer dim">
        <p>
          {t('app.rulesVersion', { version: RULES_VERSION })} ·{' '}
          {t('app.version', { version: APP_VERSION })}
        </p>
        {sharing && <p className="title-sharing">{t('menu.sharingNotice')}</p>}
      </footer>
    </main>
  );
}
